import * as React from 'react';
import { FileUp, Upload, X } from 'lucide-react';

import { Button } from '@/components/ui/button';

import { cn } from '@/lib/utils';
import { formatFileSize } from '@/lib/utils';

export interface FileDropzoneProps {
  /** Controlled selected file */
  value?: File | null;
  onChange?: (file: File | null) => void;
  /**
   * Native `accept` string passed to the hidden input, e.g. `".csv,.xlsx,.xls"`.
   * Also used for drag-drop MIME / extension validation when `acceptLabel` is not provided.
   */
  accept?: string;
  /** Human-readable list of accepted types shown in the hint, e.g. `"CSV, XLSX, XLS"` */
  acceptLabel?: string;
  /** Maximum file size in bytes. Defaults to no limit. */
  maxSize?: number;
  /** Called with an error message when the dropped/selected file fails validation */
  onError?: (message: string) => void;
  disabled?: boolean;
  /** aria-invalid styling */
  invalid?: boolean;
  className?: string;
  id?: string;
  /**
   * When provided, clicking the dropzone triggers this external input element (by id)
   * instead of rendering an internal hidden file input. Useful when a third-party
   * component (e.g. react-csv-reader) owns the actual <input>.
   */
  externalInputId?: string;
}

export function FileDropzone({
  value,
  onChange,
  accept,
  acceptLabel,
  maxSize,
  onError,
  disabled = false,
  invalid = false,
  className,
  id,
  externalInputId,
}: FileDropzoneProps) {
  const inputId = id ?? React.useId();
  const [isDragging, setIsDragging] = React.useState(false);

  const validate = (file: File): string | null => {
    if (accept) {
      const allowed = accept.split(',').map(s => s.trim().toLowerCase().replace(/^\./, ''));
      const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
      const mime = file.type.toLowerCase();
      const ok = allowed.some(a => a === ext || mime === a || mime.startsWith(a.replace('*', '')));
      if (!ok) {
        const label = acceptLabel ?? allowed.map(a => a.toUpperCase()).join(', ');
        return `Invalid file type. Accepted: ${label}`;
      }
    }
    if (maxSize && file.size > maxSize) {
      return `File too large. Maximum size is ${formatFileSize(maxSize)}.`;
    }
    return null;
  };

  const handleFile = (file: File) => {
    const error = validate(file);
    if (error) {
      onError?.(error);
      return;
    }
    onChange?.(file);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled) return;
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!disabled) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  };

  const openPicker = () => {
    if (disabled) return;
    const triggerId = externalInputId ?? inputId;
    document.getElementById(triggerId)?.click();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openPicker();
    }
  };

  const clearFile = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange?.(null);
  };

  const hint = acceptLabel
    ? `Supports ${acceptLabel}`
    : accept
      ? `Supports ${accept
          .split(',')
          .map(s => s.trim().replace(/^\./, '').toUpperCase())
          .join(', ')}`
      : undefined;

  return (
    <div className={cn('space-y-2', className)}>
      {/* Drop zone */}
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        aria-invalid={invalid ? true : undefined}
        onClick={openPicker}
        onKeyDown={handleKeyDown}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={cn(
          'relative flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-8 text-center transition-colors',
          // idle
          'border-border bg-background',
          // hover / drag-active
          'hover:border-primary hover:bg-primary/5',
          isDragging && 'border-primary bg-primary/5',
          // invalid
          invalid && 'border-destructive',
          // disabled
          disabled
            ? 'cursor-not-allowed opacity-50'
            : 'cursor-pointer focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:border-ring'
        )}
      >
        <div
          className={cn(
            'flex h-12 w-12 items-center justify-center rounded-full border border-border transition-colors',
            isDragging && 'border-primary text-primary'
          )}
        >
          <Upload className={cn('h-5 w-5 text-muted-foreground', isDragging && 'text-primary')} />
        </div>

        <div className="space-y-1">
          <p className="text-sm font-medium">{value ? value.name : 'Upload your file'}</p>
          <p className="text-xs text-muted-foreground">
            {value ? 'Click to replace' : 'Drag & drop or click to browse'}
          </p>
          {!value && hint && <p className="text-xs text-muted-foreground">{hint}</p>}
          {!value && maxSize && (
            <p className="text-xs text-muted-foreground">Max size: {formatFileSize(maxSize)}</p>
          )}
        </div>
      </div>

      {/* Hidden file input — skipped when externalInputId is provided */}
      {!externalInputId && (
        <input
          id={inputId}
          type="file"
          className="sr-only"
          accept={accept}
          disabled={disabled}
          onChange={handleInputChange}
        />
      )}

      {/* Selected file info */}
      {value && (
        <div className="flex items-center gap-3 rounded-lg border px-3 py-2.5">
          <FileUp className="h-4 w-4 shrink-0 text-muted-foreground" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{value.name}</p>
            <p className="text-xs text-muted-foreground">{formatFileSize(value.size)}</p>
          </div>
          {!disabled && (
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={clearFile}
              className="shrink-0 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Remove file</span>
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export default FileDropzone;
